/**
 * API Clients Service
 * 
 * Centralized API client instances for the CARBOwaste application.
 * These singleton instances should be used throughout the application
 * instead of creating new instances.
 * 
 * Usage:
 * ```typescript
 * import { bdoClient, carboWasteClient, dictionaryClient } from './services/ApiClients';
 * 
 * // BDO API
 * const keoData = await bdoClient.getKeoData(clientId, clientSecret, eupId, 1, 50, 2024);
 * 
 * // CarboWaste API
 * await carboWasteClient.sendContactMessage(contactRequest);
 * 
 * // Dictionary API
 * const wasteCodes = await dictionaryClient.getAllWasteCodeData();
 * ```
 */

import { 
  BdoApiClient, 
  CarboWasteApiClient, 
  DictionaryApiClient 
} from '@carbowaste/api-client';
import { config } from '../config/environment';
import i18n from '../i18n/i18n';

/**
 * BDO API Client - Polish Waste Database
 * Handles KEO, KPO, EUP operations
 */
export const bdoClient = new BdoApiClient(
  {
    appInsights: {
      instrumentationKey: config.appInsights.instrumentationKey
    }
  },
  i18n.t
);

/**
 * CarboWaste API Client - Internal services
 * Handles contact messages, templates, subscriptions
 */
export const carboWasteClient = new CarboWasteApiClient(
  {
    baseUrl: config.api.baseUrl,
    appInsights: {
      instrumentationKey: config.appInsights.instrumentationKey
    }
  },
  i18n.t
);

/**
 * Dictionary API Client - Waste codes and processes
 * Handles waste dictionaries
 */
export const dictionaryClient = new DictionaryApiClient(
  {
    baseUrl: config.api.baseUrl,
    appInsights: {
      instrumentationKey: config.appInsights.instrumentationKey
    }
  },
  i18n.t
);

/**
 * Reset BDO token state
 * Useful for logout or when switching EUP
 */
export const resetBdoToken = () => {
  bdoClient.resetTokenState();
};
